/* eslint-disable prefer-rest-params */
/**
 * extension.js Copyright (c) 2015 wochit.com
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 */
import Vue from 'vue';
import $ from 'jquery';
import _ from 'lodash-extended';
import Marionette from 'backbone.marionette';

window.URL = window.URL || window.webkitURL;

$.extend(String.prototype, {
  /**
   * Trim external \s from left
   */
  trimLeft: function () {
    return this.replace(/^\s+/g, '');
  },

  /**
   * Trim external \s from right
   */
  trimRight: function () {
    return this.replace(/\s+$/g, '');
  },

  /**
   * Trim external \s from left and right
   */
  trim: function () {
    return this.replace(/^\s+|\s+$/g, '');
  },

  /**
   * Trim external \s from left, right and then replace continious \s by single space (' ')
   */
  normalizeSpace: function () {
    return this.trim().replace(/\s+/gm, ' ');
  },

  /**
   * Perform case sensitive comparison to each argument, incapsulating OR statements. Some should match.
   * That means that comparation will continue till the first "match" occurre in case of RegExp
   * or this string value will be found "contains" in case of substring argument
   * or external function will return true in case of function argument.
   *
   * @note: Case sensitivenes may be overriden by the regular expression modifier (/.../i).
   *
   * <example>
   * // 1:
   * var str = "";
   * str.test(); // returns false; same as: if (str.length != 0)
   * // 2:
   * var str = "/example.com/Products/Default.aspx.html";
   * str.test(/\/products\//i, /\/accessories\//i) // returns true
   * </example>
   * @param(...): RegExp, string, function or boolean to test with
   * @return: boolean, true - if  ANY of the tests succeeds; false if ALL tests fails
   */
  test: function (/* ...*/) {
    if (arguments.length === 0) {
      return this.length !== 0;
    }

    for (let c = 0, length = arguments.length, type; c < length; c++) {
      // eslint-disable-next-line prefer-rest-params
      type = typeof arguments[c];

      // eslint-disable-next-line prefer-rest-params
      if ('undefined' !== type && arguments[c] instanceof RegExp) {
        // eslint-disable-next-line prefer-rest-params
        if (arguments[c].test(this)) {
          return true;
        } else {
          continue;
        }
      } else if ('string' === type) {
        // assume its a substring of `_str`
        // eslint-disable-next-line prefer-rest-params
        if (this.indexOf(arguments[c]) >= 0) {
          return true;
        } else {
          continue;
        }
      } else if ('function' === type) {
        // assumming function that accepts oneyp argument end returns boolean test result
        // eslint-disable-next-line prefer-rest-params
        if (arguments[c](this)) {
          return true;
        } else {
          continue;
        }
      } else if ('boolean' === type) {
        // eslint-disable-next-line prefer-rest-params
        if (arguments[c]) {
          return true;
        } else {
          continue;
        }
      } else {
        console.trace();
        console.error(
          'Invalid usage of extension.js@String.prototype.test(): unhandled argument type:',
          type
        );
      }
    }

    return false;
  },

  /**
   * Perform case sensitive comparison to each argument, incapsulating AND statements. All should match.
   * That means that comparation will continue till the first "not match" occurre in case of RegExp
   * or this string value will be found "not contains" in case of substring argument
   * or external function will return false in case of function argument.
   *
   * @note: Case sensitivenes may be overriden by the regular expression modifier (/.../i).
   *
   * <example>
   * // 1:
   * var str = "";
   * str.matches(); // returns false; same as: if (str.length != 0)
   * // 2:
   * var str = "/example.com/Products/Default.aspx.html";
   * str.matches(/\/products\//i, /\/accessories\//i) // returns false
   * </example>
   * @param(...): RegExp, string, function or boolean to test with
   * @return: boolean, true - if  ALL of the tests succeeds; false if ANY of tests fails
   */
  matches: function (/* ...*/) {
    if (arguments.length === 0) {
      return this.length !== 0;
    }

    for (let c = 0, length = arguments.length, type; c < length; c++) {
      type = typeof arguments[c];

      if ('undefined' !== type && arguments[c] instanceof RegExp) {
        if (arguments[c].test(this)) {
          continue;
        } else {
          return false;
        }
      } else if ('string' === type) {
        // assume a substring
        if (this.indexOf(arguments[c]) >= 0) {
          continue;
        } else {
          return false;
        }
      } else if ('function' === type) {
        // assume a function that accepts one argument end returns boolean test result
        if (arguments[c](this)) {
          continue;
        } else {
          return false;
        }
      } else if ('boolean' === type) {
        if (arguments[c]) {
          continue;
        } else {
          return false;
        }
      } else {
        console.trace();
        console.error(
          'Invalid usage of extension.js@String.prototype.matches(): unhandled argument type:',
          type
        );
      }
    }

    return true;
  },

  /**
   * Replaces each format item "{n}" in `this` string with the string equivalent of a corresponding
   * parameters object's value.
   *
   * @note: format identifier "{i}" may be repeated multiple times in any order as long as it reffers
   * to corresponding position of the argument
   *
   * <example>
   * var str = "/www.example.com/product/{0}/{0}-{1}.html".format("A1234BC", "overview");
   * // str == "/www.example.com/product/A1234BC/A1234BC-overview.html"
   * </example>
   *
   * @param(...): any type of other arguments reffered by the format string
   * @return: string, formatting result
   */
  format: function (/* ...*/) {
    let paramPosition = null;
    let rv = this;
    for (let c = 0, length = arguments.length; c < length; c++) {
      paramPosition = new RegExp('\\{' + c + '\\}', 'g');
      if (paramPosition.test(rv)) {
        rv = rv.replace(
          paramPosition,
          _.isDefined(arguments[c]) ? arguments[c].toString() : ''
        );
      }
    }

    paramPosition = null;
    return rv;
  },
});

$.extend(Number.prototype, {
  /**
   * Convert number to string in human readable format with coma-separated digit groups
   *
   * <example>
   * Math.pow(2, 32).toHumanString();       //    "4'294'967'296"
   * (123456.789).toHumanString(2, ",");    //       "123,456.79"
   * </example>
   *
   * @param(_iFixedFP): number, optional number of digits after the decimal point; by default - as is
   * @param(_chDigitGroupChar): string, optional, used as group separator symbol
   * @return: string
   */
  toHumanString: function (_iFixedFP, _chDigitGroupChar) {
    const str = _.isDefined(_iFixedFP)
      ? this.toFixed(_iFixedFP)
      : this.toString();

    const iFPIndex = str.indexOf('.');
    const iRealPartLength = iFPIndex == -1 ? str.length : iFPIndex;
    if (iRealPartLength <= 3) {
      return str;
    }

    // copying string from left to right, placing group delimiter when needed
    const sThousendDelimiter = _.isDefined(_chDigitGroupChar)
      ? _chDigitGroupChar
      : "'";
    const rv = [];
    for (let c = 0, g = iRealPartLength; c < str.length; c++, g--) {
      if (
        g % 3 === 0 &&
        c < iRealPartLength &&
        c !== 0 &&
        /[^-]/.test(str.charAt(c - 1))
      ) {
        rv.push(sThousendDelimiter);
      }

      rv.push(str.charAt(c));
    }

    return rv.join('');
  },

  /**
   * Convert number to string in human readable format, assuming that number representing a size in bytes
   * appending prefix at base 10 and separating digit groups (http://en.wikipedia.org/wiki/Kilobyte)
   *
   * <example>
   * Math.pow(2, 32).toSizeString(); // "4.29 GB"
   * </example>
   *
   * @param(_iFixedFP): number of digits after the decimal point; optional by default: 2 (#.##)
   * @return: string
   */
  toSizeString: function (_iFixedFP) {
    let n = Math.abs(this);
    // set 2 or custom fixed digits after floating point for numbers bigger than 999 byte
    _iFixedFP =
      n < 1000 ? (_iFixedFP = null) : _.isDefined(_iFixedFP) ? _iFixedFP : 2;

    const asPrefix = [
      ' B',
      ' kB',
      ' MB',
      ' GB',
      ' TB',
      ' PB',
      ' EB',
      ' ZB',
      ' YB',
    ];

    let i = 0;
    while (n >= 1000 && i < asPrefix.length - 1) {
      n /= 1000;
      ++i;
    }

    return n.toHumanString(_iFixedFP) + asPrefix[i];
  },

  toFriendlyNumber: function (digits) {
    const num = this;
    const si = [
      { value: 1e18, symbol: 'E' },
      { value: 1e15, symbol: 'P' },
      { value: 1e12, symbol: 'T' },
      { value: 1e9, symbol: 'G' },
      { value: 1e6, symbol: 'M' },
      { value: 1e3, symbol: 'k' },
    ];
    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    let i;
    for (i = 0; i < si.length; i++) {
      if (num >= si[i].value) {
        return (
          (num / si[i].value).toFixed(digits).replace(rx, '$1') + si[i].symbol
        );
      }
    }
    return num.toFixed(digits).replace(rx, '$1');
  },

  /**
   * Convert number to string in human readable format, assuming that number representing a size in bytes
   * appending prefix at base 2 and separating digit groups (http://en.wikipedia.org/wiki/Kibibyte)
   *
   * <example>
   * Math.pow(2, 32).toKibiSizeString(); // "4.00 GiB"
   * </example>
   *
   * @param(_iFixedFP): number of digits after the decimal point; optional by default: 2 (#.##)
   * @return: string
   */
  toKibiSizeString: function (_iFixedFP) {
    let n = Math.abs(this);
    // set 2 or custom fixed digits after floating point for numbers bigger than 1023 byte
    _iFixedFP =
      n < 1024 ? (_iFixedFP = null) : _.isDefined(_iFixedFP) ? _iFixedFP : 2;
    const asPrefix = [
      ' B',
      ' KiB',
      ' MiB',
      ' GiB',
      ' TiB',
      ' PiB',
      ' EiB',
      ' ZiB',
      ' YiB',
    ];
    let i = 0;

    while (n >= 1024 && i < asPrefix.length - 1) {
      n /= 1024;
      ++i;
    }

    return n.toHumanString(_iFixedFP) + asPrefix[i];
  },
});

$.extend(Boolean.prototype, {
  /**
   * Exclusive Disjunction (F T T F)
   * @return {boolean}
   */
  XOR: function (p, q) {
    return Boolean((p || q) && !(p && q));
  },

  /**
   * Logical Equality (T F F T)
   * @return {boolean}
   */
  XNOR: function (p, q) {
    return Boolean((p && q) || (!p && !q));
  },
});

/**
 * Extend jquery.js
 */

/**
 * Check if given parameter is jQuery element and not empty
 * @param $el
 * @return {boolean}
 */
$.isElement = function ($el) {
  return $el instanceof $ && 0 < $el.length;
};

$.fn.extend({
  /**
   * Check if element is editable, only affects the first matched element
   * @return {boolean}
   */
  isEditable: function () {
    const el = this[0];
    let rv = false;

    if (el) {
      if ($(el).is('input:not([type=radio]):not([type=checkbox]),textarea')) {
        rv = !el.readOnly && !el.disabled;
      } else {
        rv = 'true' === el.contentEditable;
      }
    }

    return rv;
  },

  /**
   * Extract element selector string, only affects the first matched element (need many - use loop)
   *
   * @example
   * $("<input type='button' class='btn js-btn-a'/>").getSelector({tag: true, classes: true});
   *      "INPUT.btn.js-btn-a"
   *
   * @param options {Object} - distinguishing configuration, refer to comments below
   * @return {string}
   */
  getSelector: function (options) {
    options = $.extend(
      {
        path: false, // (HTML>BODY>FORM>)INPUT#btn1.btn-large[attr="value"],
        tag: true, // HTML>BODY>FORM>(INPUT)#btn1.btn-large[attr="value"]
        id: true, // HTML>BODY>FORM>INPUT(#btn1).btn-large[attr="value"]
        classes: false, // HTML>BODY>FORM>INPUT#btn1(.btn-large)[attr="value"]
        attributes: false, // HTML>BODY>FORM>INPUT#btn1.btn-large([attr="value"])
        data: false, // include (data-*) attribute, if true then implies attributes also true
        style: false, // include (style) attribute, if true then implies attributes also true
      },
      options
    );
    const el = this[0];
    const rv = [];
    let i;
    let I;
    let attr;
    let className;

    if (!el) {
      return '';
    }

    if (!options.attributes && (options.data || options.style)) {
      options.attributes = true;
    }

    if (options.path) {
      const pathOptions = $.extend({}, options, { path: false, tag: true });
      const path = $(el)
        .parents()
        .map(function () {
          return $(this).getSelector(pathOptions);
        })
        .get()
        .reverse()
        .concat('')
        .join('>');
      rv.push(path);
    }

    if (options.tag && el.tagName) {
      rv.push(el.tagName);
    }

    if (options.id && el.id) {
      rv.push('#');
      rv.push(el.id);
    }

    if (options.classes && el.classList) {
      for (i = 0, I = el.classList.length; i < I; i++) {
        className = el.classList[i];
        rv.push('.');
        rv.push(className);
      }
    }

    if (options.attributes && el.attributes) {
      for (i = 0, I = el.attributes.length; i < I; i++) {
        attr = el.attributes[i];
        if ('id' === attr.name || 'class' === attr.name) {
          continue; // handled
        }
        if (!options.data && attr.name.indexOf('data-') === 0) {
          continue;
        }
        if (!options.style && 'style' === attr.name) {
          continue;
        }

        rv.push('[');
        rv.push(attr.name);
        rv.push('="');
        rv.push(attr.value); // ToDo: escape?
        rv.push('"]');
      }
    }

    return rv.join('');
  },

  isScrollable: function () {
    return 0 < this[0].scrollHeight - this.innerHeight();
  },
  /**
   * calculates and checks if the element scrolling has reached the bottom
   * @param margin the padding of outer box if necessary. default (100)
   * @return {boolean}
   */
  isScrollAtBottom: function (margin) {
    const left2bottom =
      this[0].scrollHeight - this.innerHeight() - this.scrollTop();
    margin = margin || 100;
    return margin > left2bottom;
  },
  /**
   * Determine if an element is in displayed/visible bounds of his vertically scrollable container
   * @param $container optional
   * @return {boolean}
   */
  isInVerticalScrollArea: function ($container) {
    $container = $container || this.parent();
    const o = {};
    o.containerTop = $container.scrollTop();
    o.containerBottom = o.containerTop + $container.innerHeight();
    o.thisTop = o.containerTop + this.position().top;
    o.thisBottom = o.thisTop + this.outerHeight();

    return o.thisTop >= o.containerTop && o.thisBottom <= o.containerBottom;
  },

  scrollTo: function (target, options, callback) {
    if (typeof options == 'function' && arguments.length == 2) {
      callback = options;
      options = target;
    }
    const settings = $.extend(
      {
        scrollTarget: target,
        offsetTop: 50,
        duration: 500,
        easing: 'swing',
      },
      options
    );
    return this.each(() => {
      const scrollPane = $(this);
      const scrollTarget =
        typeof settings.scrollTarget == 'number'
          ? settings.scrollTarget
          : $(settings.scrollTarget);
      const scrollY =
        typeof scrollTarget == 'number'
          ? scrollTarget
          : scrollTarget.offset().top +
            scrollPane.scrollTop() -
            parseInt(settings.offsetTop);
      scrollPane.animate(
        { scrollTop: scrollY },
        parseInt(settings.duration),
        settings.easing,
        function () {
          if (typeof callback == 'function') {
            callback.call(this);
          }
        }
      );
    });
  },

  attachTo: function ($target, how) {
    if (!this.length) {
      return;
    }

    const onResize = function (e) {
      if (!$.contains(document, this[0])) {
        // unsubscribe if element is removed from the DOM
        $(window).off(e);
        return;
      }

      const css = { position: 'fixed' };
      $.extend(css, {
        top: $target.offset().top + (how.top ? how.top : 0),
        left: $target.offset().left + (how.left ? how.left : 0),
      });
      this.css(css);
    };

    onResize.call(this);
    $(window).on('resize.attachTo', onResize.bind(this));
  },

  /**
   * rediscover element using it's own selector, reusing it's context if present
   * modifies this content, supports chaining
   * @return {jQuery}
   */
  discover: function () {
    if (this.selector) {
      this.splice(0, this.length);
      (this.context ? $(this.context) : $).find(this.selector).each(
        function (i, el) {
          this.push(el);
        }.bind(this)
      );
    }
    return this;
  },

  visible: function () {
    return this.css('visibility', 'visible');
  },

  invisible: function () {
    return this.css('visibility', 'hidden');
  },
});

/**
 * Extend backbone.js
 */
$.extend(Backbone.Collection.prototype, {
  /**
   * Multifield comparator for Backbone Collection of models
   * @requires `comparatorFields` in form [{asc: {boolean}, from: {function(model)}}, ...]
   */
  comparator: function (m1, m2) {
    if (this.comparatorFields) {
      for (let i = 0, I = this.comparatorFields.length, v1, v2; i < I; i++) {
        v1 = this.comparatorFields[i].from.call(this, m1);
        v2 = this.comparatorFields[i].from.call(this, m2);
        if (v1 !== v2) {
          return Boolean().XNOR(v1 > v2, this.comparatorFields[i].asc) ? 1 : -1;
        }
      }
    }
    return 0;
  },
});

$.extend(Backbone.View.prototype, {
  /**
   * Check if view element is attached to DOM
   * @return {*|boolean}
   */
  isInDOM: function () {
    return this.el && $.contains(document, this.el);
  },

  /**
   * Check if view element is attached to DOM and visible
   * @return {*|{sot, token, silence, placeholder, love}}
   */
  isVisible: function () {
    return this.$el && this.$el.is(':visible');
  },

  /**
   * Custom close function that triggers close events for subviews
   */
  close: function () {
    const verbose = !!0; // true
    if (verbose) {
      let viewName =
        (this.el && this.el.id) ||
        (this.el && this.el.className) ||
        (this.$el && this.$el.selector) ||
        this.cid;
      if (!viewName) {
        viewName = 'undefined';
      }
      console.group('close', viewName);
    }

    // execute custom close procedure

    try {
      this.onClose && this.onClose();
    } catch (bug) {
      console.trace(bug);
    }

    // delegate close event to other subviews
    _.each(this._views, function (view) {
      if (!view) {
        return true; // continue;
      }
      try {
        if (view instanceof Vue) {
          view.$destroy();
          view.$el.remove();
        } else if (view instanceof Marionette.ItemView) {
          view.destroy();
        } else if (view instanceof Backbone.View) {
          view.close();
        } else {
          console.warn('destructor not implemented', view);
        }
      } catch (bug) {
        console.trace(bug);
      }
    });
    this._views = null;

    this.off();
    app.off(null, null, this);

    let state = this.viewState || this.state;
    if (state) {
      this.stopListening(state, null, null);
      state.off();
      state = null;
    }

    if (this.task) {
      this.stopListening(this.task, null, null);
    }

    this.remove();
    if (verbose) {
      console.groupEnd();
    }
  },
});

$.extend(Backbone.Model.prototype, {
  // make readonly copy of current arguments
  readonly: function () {
    return _.readonly(this.attributes);
  },
});

/**
 * Extend backbone.marionette.2.4.2.js
 */
Marionette.Behaviors.behaviorsLookup = function () {
  return app.namespace.Behavior;
};

/**
 * Override some `Marionette.Region` methods to support
 * showing Vue.js view instances within layout region.
 */
$.extend(Marionette.Region.prototype, {
  show: function (view, options) {
    if (!this._ensureElement()) {
      return;
    }

    this._ensureViewIsIntact(view);

    const showOptions = options || {};
    const isDifferentView = view !== this.currentView;
    const preventDestroy = !!showOptions.preventDestroy;
    const forceShow = !!showOptions.forceShow;

    // We are only changing the view if there is a current view to change to begin with
    const isChangingView = !!this.currentView;

    // Only destroy the current view if we don't want to `preventDestroy` and if
    // the view given in the first argument is different than `currentView`
    const _shouldDestroyView = isDifferentView && !preventDestroy;

    // Only show the view given in the first argument if it is different than
    // the current view or if we want to re-show the view. Note that if
    // `_shouldDestroyView` is true, then `_shouldShowView` is also necessarily true.
    const _shouldShowView = isDifferentView || forceShow;

    if (isChangingView) {
      this.triggerMethod('before:swapOut', this.currentView, this, options);
    }

    if (this.currentView) {
      delete this.currentView._parent;
    }

    if (_shouldDestroyView) {
      this.empty();

      // A `destroy` event is attached to the clean up manually removed views.
      // We need to detach this event when a new view is going to be shown as it
      // is no longer relevant.
    } else if (isChangingView && _shouldShowView) {
      if (this.currentView instanceof Vue) {
        this.currentView.$off('hook:destroyed', this.empty);
      } else {
        this.currentView.off('destroy', this.empty, this);
      }
    }

    if (_shouldShowView) {
      // We need to listen for if a view is destroyed
      // in a way other than through the region.
      // If this happens we need to remove the reference
      // to the currentView since once a view has been destroyed
      // we can not reuse it.
      if (view instanceof Vue) {
        view.$once('hook:destroyed', this.empty);
        view.$mount();
      } else {
        view.once('destroy', this.empty, this);
        view.render();
      }

      view._parent = this;

      if (isChangingView) {
        this.triggerMethod('before:swap', view, this, options);
      }

      this.triggerMethod('before:show', view, this, options);
      Marionette.triggerMethodOn(view, 'before:show', view, this, options);

      if (isChangingView) {
        this.triggerMethod('swapOut', this.currentView, this, options);
      }

      // An array of views that we're about to display
      const attachedRegion = Marionette.isNodeAttached(this.el);

      // The views that we're about to attach to the document
      // It's important that we prevent _getNestedViews from being executed unnecessarily
      // as it's a potentially-slow method
      let displayedViews = [];

      const attachOptions = _.extend(
        {
          triggerBeforeAttach: this.triggerBeforeAttach,
          triggerAttach: this.triggerAttach,
        },
        showOptions
      );

      if (attachedRegion && attachOptions.triggerBeforeAttach) {
        displayedViews = this._displayedViews(view);
        this._triggerAttach(displayedViews, 'before:');
      }

      this.attachHtml(view);
      this.currentView = view;

      if (attachedRegion && attachOptions.triggerAttach) {
        displayedViews = this._displayedViews(view);
        this._triggerAttach(displayedViews);
      }

      if (isChangingView) {
        this.triggerMethod('swap', view, this, options);
      }

      this.triggerMethod('show', view, this, options);
      Marionette.triggerMethodOn(view, 'show', view, this, options);

      return this;
    }

    return this;
  },

  // Override this method to change how the new view is
  // appended to the `$el` that the region is managing
  attachHtml: function (view) {
    this.$el.contents().detach();

    if (view instanceof Vue) {
      this.el.appendChild(view.$el);
    } else {
      this.el.appendChild(view.el);
    }
  },

  // Destroy the current view, if there is one. If there is no
  // current view, it does nothing and returns immediately.
  empty: function (options) {
    options = $.extend(
      {
        preventDestroy: false,
      },
      options
    );
    const view = this.currentView;

    // If there is no view in the region
    // we should not remove anything
    if (!view) {
      return;
    }

    if (view instanceof Vue) {
      view.$off('hook:destroyed', this.empty);
    } else {
      view.off('destroy', this.empty, this);
      this.triggerMethod('before:empty', view);
    }

    if (!options.preventDestroy) {
      this._destroyView();
    }
    this.triggerMethod('empty', view);

    // Remove region pointer to the currentView
    this.currentView = null;
    delete this.currentView;

    if (options.preventDestroy) {
      this.$el.contents().detach();
    }

    return this;
  },

  // call 'destroy' or 'remove', depending on which is found
  // on the view (if showing a raw Backbone view or a Marionette View)
  _destroyView: function () {
    const view = this.currentView;

    if (view instanceof Vue) {
      view.$destroy();
      view.$el.remove();
    } else if (view.destroy && !view.isDestroyed) {
      view.destroy();
    } else if (view.remove) {
      view.remove();

      // appending isDestroyed to raw Backbone View allows regions
      // to throw a ViewDestroyedError for this view
      view.isDestroyed = true;
    }
  },
});
